import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { validLangs } from '@/mocks/languages';
import { articles } from '@/mocks/articles';

export const SITE_URL: string =
  import.meta.env.VITE_SITE_URL || 'https://allchinabuyspread.skin';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50cb04cb08f38590896596985.jpeg';

export const OG_IMAGE_HOME =
  'https://readdy.ai/api/search-image?query=A%20stylish%20editorial%20scene%20of%20folded%20streetwear%20garments%20and%20a%20pair%20of%20sneakers%20arranged%20on%20a%20warm%20terracotta%20and%20cream%20backdrop%20with%20soft%20dramatic%20natural%20light%20varied%20fabric%20textures%20premium%20fashion%20product%20photography%20rich%20warm%20tones&width=1600&height=900&seq=hero-allchinabuy-2026&orientation=landscape';

type Lang = 'en' | 'fr' | 'da' | 'de';

export interface RouteInfo {
  lang: Lang;
  slug: string | null;
}

export function parseRoute(pathname: string): RouteInfo {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return { lang: 'en', slug: null };
  }
  if (validLangs.includes(parts[0])) {
    const lang = parts[0] as Lang;
    return { lang, slug: parts.length > 1 ? parts[1] : null };
  }
  return { lang: 'en', slug: parts[0] };
}

const HOME_SEO: Record<Lang, { title: string; description: string; keywords: string }> = {
  en: {
    title: 'AllChinaBuy Spreadsheet 2026 – Finds, Guides, QC & Shipping',
    description:
      'Explore the AllChinaBuy Spreadsheet hub for product finds, category guides, QC tips, shipping costs and honest reviews. Compare options and buy smarter with practical buying resources.',
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy Review, AllChinaBuy Shipping Cost, AllChinaBuy Coupon, AllChinaBuy Guide',
  },
  fr: {
    title: 'AllChinaBuy Spreadsheet 2026 – Trouvailles, guides, QC et livraison',
    description:
      'Explorez le hub AllChinaBuy Spreadsheet : trouvailles de produits, guides par catégorie, conseils QC, frais de livraison et avis honnêtes pour acheter plus malin.',
    keywords:
      'AllChinaBuy Spreadsheet, Avis AllChinaBuy, Frais de livraison AllChinaBuy, Coupon AllChinaBuy',
  },
  da: {
    title: 'AllChinaBuy Spreadsheet 2026 – Fund, guider, QC og forsendelse',
    description:
      'Udforsk AllChinaBuy Spreadsheet-hubben med produktfund, kategoriguider, QC-tips, forsendelsesomkostninger og ærlige anmeldelser for at købe smartere.',
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy anmeldelse, AllChinaBuy forsendelse, AllChinaBuy kupon',
  },
  de: {
    title: 'AllChinaBuy Spreadsheet 2026 – Funde, Ratgeber, QC und Versand',
    description:
      'Entdecke den AllChinaBuy Spreadsheet-Hub mit Produktempfehlungen, Kategorie-Ratgebern, QC-Tipps, Versandkosten und ehrlichen Bewertungen, um cleverer zu kaufen.',
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy Bewertung, AllChinaBuy Versandkosten, AllChinaBuy Gutschein',
  },
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo(): void {
  const location = useLocation();

  useEffect(() => {
    const route = parseRoute(location.pathname);
    const { lang, slug } = route;

    let title: string;
    let description: string;
    let keywords: string;
    let canonical: string;
    let ogImage: string;
    let ogType: string;

    if (slug) {
      const article = articles.find((a) => a.slug === slug);
      if (article) {
        title = `${article.title[lang]} – AllChinaBuy Spreadsheet`;
        description = article.summary[lang];
        keywords = `AllChinaBuy Spreadsheet, ${article.title[lang]}, AllChinaBuy ${article.category[lang]}`;
        canonical = `${SITE_URL}/${lang}/${slug}`;
        ogImage = article.image;
        ogType = 'article';
      } else {
        title = '404 – AllChinaBuy Spreadsheet';
        description = 'The page you are looking for does not exist.';
        keywords = 'AllChinaBuy Spreadsheet';
        canonical = `${SITE_URL}/`;
        ogImage = OG_IMAGE_HOME;
        ogType = 'website';
      }
    } else {
      const home = HOME_SEO[lang];
      title = home.title;
      description = home.description;
      keywords = home.keywords;
      canonical = lang === 'en' ? `${SITE_URL}/` : `${SITE_URL}/${lang}`;
      ogImage = OG_IMAGE_HOME;
      ogType = 'website';
    }

    document.title = title;
    document.documentElement.lang = lang;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);
  }, [location.pathname]);
}